/**
 * faq-animate.js — Animated FAQ accordion for Safer Lighting
 *
 * Animates HEIGHT ONLY (no opacity) so CSS !important rules on sibling
 * properties can never block the animation from running.
 *
 * Handles two accordion patterns:
 *
 * 1. <details>/<summary> sections (faq.liquid, faq-accordion.liquid,
 *    affiliate-program.liquid):
 *    - .faq-accordion__item / .faq-accordion__answer
 *    - .sl-faq__item / .sl-faq__a
 *    - .sl-tier-faq__item / .sl-tier-faq__body
 *    - .sl-aff__q / (direct child <p>)
 *
 * 2. Product-page dl.product-faq <dt>/<dd> pairs:
 *    Converts each dt/dd pair into an animated accordion with a gold chevron.
 *    All dd elements collapsed by default; click dt to expand.
 *
 * No-JS fallback: native <details> or visible dl/dt/dd (no animation, accessible).
 * prefers-reduced-motion: instant open/close.
 */
(function () {
  'use strict';

  const OPEN_MS    = 340;
  const CLOSE_MS   = 240;
  const OPEN_EASE  = 'cubic-bezier(0.16, 1, 0.3, 1)'; // expo-out spring
  const CLOSE_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  const PAIRS = [
    { item: '.faq-accordion__item', body: '.faq-accordion__answer' },
    { item: '.sl-faq__item',        body: '.sl-faq__a' },
    { item: '.sl-tier-faq__item',   body: '.sl-tier-faq__body' },
    { item: '.sl-aff__q',           body: ':scope > p' },
  ];

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ── Chevron SVG for product-page dl ──────────────────────────────
  const CHEVRON_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>';

  // ── Animate a body element's height ──────────────────────────────
  function animateHeight(el, fromPx, toPx, duration, easing, onDone) {
    if (reducedMotion) {
      el.style.height = toPx === 0 ? '0px' : 'auto';
      onDone && onDone();
      return null;
    }
    el.style.height = fromPx + 'px';
    const anim = el.animate(
      [{ height: fromPx + 'px' }, { height: toPx + 'px' }],
      { duration, easing, fill: 'forwards' }
    );
    anim.onfinish = () => { onDone && onDone(); };
    return anim;
  }

  // ── <details>/<summary> handler ──────────────────────────────────
  function initItem(details) {
    if (details._faqAnimated) return;
    details._faqAnimated = true;

    const summary = details.querySelector('summary');
    let body = null;
    for (const { item, body: bSel } of PAIRS) {
      if (details.matches(item)) { body = details.querySelector(bSel); break; }
    }
    if (!summary || !body) return;

    const inner = document.createElement('div');
    inner.className = 'faq-inner-wrap';
    const comp = window.getComputedStyle(body);
    inner.style.paddingTop = comp.paddingTop;
    inner.style.paddingRight = comp.paddingRight;
    inner.style.paddingBottom = comp.paddingBottom;
    inner.style.paddingLeft = comp.paddingLeft;
    body.style.padding = '0';

    while (body.firstChild) inner.appendChild(body.firstChild);
    body.appendChild(inner);

    body.style.overflow = 'hidden';

    let anim   = null;
    let isOpen = details.hasAttribute('open');

    summary.addEventListener('click', (e) => {
      e.preventDefault();
      if (anim) { anim.cancel(); anim = null; }

      if (isOpen) {
        // Close: measure current height, animate to 0
        const h = body.scrollHeight;
        body.style.height = h + 'px';
        isOpen = false;
        anim = animateHeight(body, h, 0, CLOSE_MS, CLOSE_EASE, () => {
          details.removeAttribute('open');
          body.style.height = '';
          anim = null;
        });
      } else {
        // Open: reveal in DOM first so scrollHeight is measurable
        details.setAttribute('open', '');
        body.style.height = '0px';
        const h = body.scrollHeight;
        isOpen = true;
        anim = animateHeight(body, 0, h, OPEN_MS, OPEN_EASE, () => {
          body.style.height = 'auto';
          anim = null;
        });
      }
    });
  }

  // ── Product-page dl.product-faq handler ──────────────────────────
  function initProductFAQ(dl) {
    if (dl._faqInited) return;
    dl._faqInited = true;

    const pairs = [];
    let pendingDt = null;
    for (const child of Array.from(dl.children)) {
      if (child.tagName === 'DT')                     { pendingDt = child; }
      else if (child.tagName === 'DD' && pendingDt)   { pairs.push({ dt: pendingDt, dd: child }); pendingDt = null; }
    }
    if (!pairs.length) return;

    pairs.forEach(({ dt, dd }, i) => {
      // Wrap dd content for height animation (padding lives inside)
      const inner = document.createElement('div');
      inner.className = 'faq-inner';
      while (dd.firstChild) inner.appendChild(dd.firstChild);
      dd.appendChild(inner);

      // Accessibility
      const qId = 'pFaq-q-' + i;
      const aId = 'pFaq-a-' + i;
      dt.setAttribute('role', 'button');
      dt.setAttribute('tabindex', '0');
      dt.setAttribute('aria-expanded', 'false');
      dt.setAttribute('aria-controls', aId);
      dt.id = qId;
      dd.setAttribute('role', 'region');
      dd.setAttribute('aria-labelledby', qId);
      dd.id = aId;

      // Gold chevron
      const chevron = document.createElement('span');
      chevron.className = 'faq-chevron';
      chevron.innerHTML = CHEVRON_SVG;
      dt.appendChild(chevron);

      // Start collapsed
      dd.style.height   = '0px';
      dd.style.overflow = 'hidden';

      let anim   = null;
      let isOpen = false;

      function toggle() {
        if (anim) { anim.cancel(); anim = null; }

        if (isOpen) {
          const h = dd.scrollHeight;
          dd.style.height = h + 'px';
          isOpen = false;
          dt.setAttribute('aria-expanded', 'false');
          anim = animateHeight(dd, h, 0, CLOSE_MS, CLOSE_EASE, () => {
            dd.style.height = '0px';
            anim = null;
          });
        } else {
          dd.style.height = '0px';
          const h = inner.scrollHeight + 28; // account for .faq-inner padding
          isOpen = true;
          dt.setAttribute('aria-expanded', 'true');
          anim = animateHeight(dd, 0, h, OPEN_MS, OPEN_EASE, () => {
            dd.style.height = 'auto';
            anim = null;
          });
        }
      }

      dt.addEventListener('click', toggle);
      dt.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
      });
    });
  }

  function initAll() {
    PAIRS.forEach(({ item }) => {
      document.querySelectorAll(item).forEach(initItem);
    });
    document.querySelectorAll('dl.product-faq').forEach(initProductFAQ);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Catch FAQ sections injected after page load
  new MutationObserver((mutations) => {
    for (const mut of mutations) {
      for (const node of mut.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        PAIRS.forEach(({ item }) => {
          if (node.matches && node.matches(item))   initItem(node);
          if (node.querySelectorAll)                node.querySelectorAll(item).forEach(initItem);
        });
        if (node.matches && node.matches('dl.product-faq'))   initProductFAQ(node);
        if (node.querySelectorAll) node.querySelectorAll('dl.product-faq').forEach(initProductFAQ);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
